import React, { useState } from 'react';
import { View, Text, ScrollView, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';

const steps = [
  'Relationship with Money',
  'Goals + Vision',
  'Wealth Strategy + Lifestyle',
  'Risk + Philosophy',
  'Personal Passions + Preferences',
  'Personality Type Matching',
];

const initialAnswers = {
  // Section 1
  q1: '', q2: '', q3: '', q4: '', q5: '',
  // Section 2
  q6: '', q7_1: '', q7_3: '', q7_5: '', q7_10: '', q7_20: '', q8: '', q9: '', q10: '',
  // Section 3
  q11: [], q11_other: '', q12: '', q13: '', q14: '',
  // Section 4
  q15: '', q16: '', q17: '', q18: '',
  // Section 5
  q19: '', q20: '', q21: '', q22: '', q23: '',
  // Section 6
  q24: '', q25: [], q26: '',
};

const wealthOptions = [
  'Real estate',
  'Startups',
  'Private equity',
  'Venture capital',
  'Stock market',
  'Owning a business',
  'Other',
];

const personalityOptions = [
  "I'm analytical — I love numbers and logic",
  "I'm a dreamer — I think big and see the vision",
  "I'm a doer — I take fast action and adapt on the fly",
  "I'm cautious — I like to think things through thoroughly",
  "I'm creative — I value freedom and out-of-the-box ideas",
];

const CheckboxItem = ({ label, checked, onPress }) => (
  <TouchableOpacity style={styles.checkboxItem} onPress={onPress}>
    <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
      {checked && <Text style={styles.checkmark}>✓</Text>}
    </View>
    <Text style={styles.checkboxLabel}>{label}</Text>
  </TouchableOpacity>
);

const Onboarding = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckbox = (field, option) => {
    setAnswers((prev) => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(option)
          ? arr.filter((o) => o !== option)
          : [...arr, option],
      };
    });
  };

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = () => {
    // Save onboarding completion status to localStorage
    localStorage.setItem('zecher_onboarding_completed', 'true');
    localStorage.setItem('zecher_onboarding_answers', JSON.stringify(answers));
    
    // Notify the parent component that onboarding is complete
    if (onComplete) {
      onComplete();
    }
    
    // Navigate to dashboard
    navigate('/');
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <img 
            src="/zecher-logo.png" 
            alt="Zecher"
            style={styles.logoImage}
            onError={(e) => {
              // Fallback to text logo if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <View style={[styles.logoFallback, { display: 'none' }]}>
            <Text style={styles.logoText}>Zecher</Text>
          </View>
        </View>
        <Text style={styles.brandTitle}>Welcome</Text>
        <Text style={styles.subtitle}>Let's get to know you and your financial goals</Text>
      </View>

      {/* Progress Indicator */}
      <View style={styles.progressContainer}>
        {steps.map((label, idx) => (
          <View key={label} style={styles.progressStep}>
            <View style={[
              styles.progressCircle,
              idx === step ? styles.progressCircleActive : 
              idx < step ? styles.progressCircleCompleted : styles.progressCircleInactive
            ]}>
              <Text style={[
                styles.progressNumber,
                idx === step ? styles.progressNumberActive :
                idx < step ? styles.progressNumberCompleted : styles.progressNumberInactive
              ]}>
                {idx + 1}
              </Text>
            </View>
            {idx < steps.length - 1 && <View style={styles.progressLine} />}
          </View>
        ))}
      </View>

      {/* Step Content */}
      <View style={styles.stepContainer}>
        {/* Step 1: Relationship with Money */}
        {step === 0 && (
          <View>
            <Text style={styles.stepTitle}>Your Relationship with Money</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>1. What's important to you about money?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q1} 
                onChangeText={(text) => handleChange('q1', text)}
                placeholder="Tell us what matters most to you about money..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>2. How would you describe your current relationship with money in one word? Why?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q2} 
                onChangeText={(text) => handleChange('q2', text)}
                placeholder="One word and explanation..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>3. When you think about money growing up, what stands out the most?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q3} 
                onChangeText={(text) => handleChange('q3', text)}
                placeholder="Share a memory or experience..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>4. What's one money habit you're proud of — and one you'd like to improve?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q4} 
                onChangeText={(text) => handleChange('q4', text)}
                placeholder="Proud of... Would like to improve..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>5. What does "financial peace of mind" look like to you?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q5} 
                onChangeText={(text) => handleChange('q5', text)}
                placeholder="Describe your vision of financial peace..."
              />
            </View>
          </View>
        )}

        {/* Step 2: Goals + Vision */}
        {step === 1 && (
          <View>
            <Text style={styles.stepTitle}>Your Goals + Vision</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>6. What are the top 1–3 things you'd like to gain from going through this process?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q6} 
                onChangeText={(text) => handleChange('q6', text)}
                placeholder="What are you hoping to achieve?"
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>7. What do you want to accomplish — personally, professionally, and financially — in the next:</Text>
              <View style={styles.timelineGrid}>
                <TextInput 
                  style={styles.timelineInput}
                  placeholder="1 year" 
                  value={answers.q7_1} 
                  onChangeText={(text) => handleChange('q7_1', text)}
                />
                <TextInput 
                  style={styles.timelineInput}
                  placeholder="3 years" 
                  value={answers.q7_3} 
                  onChangeText={(text) => handleChange('q7_3', text)}
                />
                <TextInput 
                  style={styles.timelineInput}
                  placeholder="5 years" 
                  value={answers.q7_5} 
                  onChangeText={(text) => handleChange('q7_5', text)}
                />
                <TextInput 
                  style={styles.timelineInput}
                  placeholder="10 years" 
                  value={answers.q7_10} 
                  onChangeText={(text) => handleChange('q7_10', text)}
                />
                <TextInput 
                  style={styles.timelineInput}
                  placeholder="20 years" 
                  value={answers.q7_20} 
                  onChangeText={(text) => handleChange('q7_20', text)}
                />
              </View>
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>8. When do you ideally want to retire?</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q8} 
                onChangeText={(text) => handleChange('q8', text)}
                placeholder="Age or timeframe..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>9. How much would you like to have saved by retirement?</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q9} 
                onChangeText={(text) => handleChange('q9', text)}
                placeholder="Target amount..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>10. What does a fulfilling life look like to you — beyond just money?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q10} 
                onChangeText={(text) => handleChange('q10', text)}
                placeholder="Describe your ideal fulfilling life..."
              />
            </View>
          </View>
        )}

        {/* Step 3: Wealth Strategy + Lifestyle */}
        {step === 2 && (
          <View>
            <Text style={styles.stepTitle}>Wealth Strategy + Lifestyle</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>11. Are you interested in building wealth through any of the following?</Text>
              <View style={styles.checkboxGrid}>
                {wealthOptions.map((opt) => (
                  <CheckboxItem
                    key={opt}
                    label={opt}
                    checked={answers.q11.includes(opt)}
                    onPress={() => handleCheckbox('q11', opt)}
                  />
                ))}
              </View>
              {answers.q11.includes('Other') && (
                <TextInput 
                  style={[styles.textInput, styles.marginTop]}
                  placeholder="Please specify other..." 
                  value={answers.q11_other} 
                  onChangeText={(text) => handleChange('q11_other', text)}
                />
              )}
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>12. Why do those paths appeal to you?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q12} 
                onChangeText={(text) => handleChange('q12', text)}
                placeholder="What draws you to these approaches?"
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>13. Do you want to achieve financial independence? What would that allow you to do?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q13} 
                onChangeText={(text) => handleChange('q13', text)}
                placeholder="Your vision of financial independence..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>14. What do you want your money to do for you — in the short term and long term?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q14} 
                onChangeText={(text) => handleChange('q14', text)}
                placeholder="Short term goals... Long term goals..."
              />
            </View>
          </View>
        )}

        {/* Step 4: Risk + Philosophy */}
        {step === 3 && (
          <View>
            <Text style={styles.stepTitle}>Risk + Philosophy</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>15. How do you feel about taking financial risks? (Give a real-life example if you have one)</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q15} 
                onChangeText={(text) => handleChange('q15', text)}
                placeholder="Your comfort with risk and examples..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>16. Would you describe yourself as more conservative or more aggressive with money? Why?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q16} 
                onChangeText={(text) => handleChange('q16', text)}
                placeholder="Conservative or aggressive and reasoning..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>17. What are your views on debt? When is it good vs bad?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q17} 
                onChangeText={(text) => handleChange('q17', text)}
                placeholder="Your philosophy on debt..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>18. What keeps you up at night when it comes to money or your future?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q18} 
                onChangeText={(text) => handleChange('q18', text)}
                placeholder="Your financial worries or concerns..."
              />
            </View>
          </View>
        )}

        {/* Step 5: Personal Passions + Preferences */}
        {step === 4 && (
          <View>
            <Text style={styles.stepTitle}>Personal Passions + Preferences</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>19. What are you passionate about? What makes you feel most alive?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q19} 
                onChangeText={(text) => handleChange('q19', text)}
                placeholder="Your passions and what energizes you..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>20. What do you value most in a financial advisor or partner?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q20} 
                onChangeText={(text) => handleChange('q20', text)}
                placeholder="What qualities matter most to you?"
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>21. Have you worked with a financial advisor before? If yes, what did you like/dislike?</Text>
              <TextInput 
                style={styles.textArea}
                multiline
                numberOfLines={4}
                value={answers.q21} 
                onChangeText={(text) => handleChange('q21', text)}
                placeholder="Your previous experience..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>22. What kind of communication do you prefer? (e.g., text, phone, Zoom, in-person)</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q22} 
                onChangeText={(text) => handleChange('q22', text)}
                placeholder="Your preferred communication style..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>23. How involved do you want to be in your financial strategy? (Hands-on, collaborative, or hands-off)</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q23} 
                onChangeText={(text) => handleChange('q23', text)}
                placeholder="Your preferred level of involvement..."
              />
            </View>
          </View>
        )}

        {/* Step 6: Personality Type Matching */}
        {step === 5 && (
          <View>
            <Text style={styles.stepTitle}>Personality Type Matching</Text>
            
            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>24. If your money were a person, how would you describe its personality?</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q24} 
                onChangeText={(text) => handleChange('q24', text)}
                placeholder="Describe your money's personality..."
              />
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>25. Which of these describes you best? (Pick any that apply)</Text>
              <View style={styles.checkboxList}>
                {personalityOptions.map((opt) => (
                  <CheckboxItem
                    key={opt}
                    label={opt}
                    checked={answers.q25.includes(opt)}
                    onPress={() => handleCheckbox('q25', opt)}
                  />
                ))}
              </View>
            </View>

            <View style={styles.questionContainer}>
              <Text style={styles.questionLabel}>26. What kind of person do you work best with? (E.g., structured, energetic, detail-oriented, strategic, etc.)</Text>
              <TextInput 
                style={styles.textInput}
                value={answers.q26} 
                onChangeText={(text) => handleChange('q26', text)}
                placeholder="Describe your ideal working partner..."
              />
            </View>
          </View>
        )}
      </View>

      {/* Navigation Buttons */}
      <View style={styles.navigationContainer}>
        <TouchableOpacity 
          style={[styles.button, styles.backButton, step === 0 && styles.buttonDisabled]} 
          onPress={back} 
          disabled={step === 0}
        >
          <Text style={[styles.buttonText, styles.backButtonText, step === 0 && styles.buttonTextDisabled]}>
            Back
          </Text>
        </TouchableOpacity>
        
        {step < steps.length - 1 ? (
          <TouchableOpacity style={[styles.button, styles.nextButton]} onPress={next}>
            <Text style={[styles.buttonText, styles.nextButtonText]}>Next</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={[styles.button, styles.submitButton]} onPress={handleSubmit}>
            <Text style={[styles.buttonText, styles.submitButtonText]}>Complete Setup</Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 768,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  logoImage: {
    height: 60,
    width: 'auto',
    maxWidth: 200,
    objectFit: 'contain',
    border: 'none',
    outlineStyle: 'none',
    backgroundColor: 'transparent',
  },
  logoFallback: {
    padding: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  brandTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#2563EB',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 40,
    paddingHorizontal: 16,
  },
  progressStep: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressCircleInactive: {
    backgroundColor: '#D1D5DB',
  },
  progressCircleActive: {
    backgroundColor: '#2563EB',
  },
  progressCircleCompleted: {
    backgroundColor: '#059669',
  },
  progressNumber: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  progressNumberInactive: {
    color: '#FFFFFF',
  },
  progressNumberActive: {
    color: '#FFFFFF',
  },
  progressNumberCompleted: {
    color: '#FFFFFF',
  },
  progressLine: {
    flex: 1,
    height: 2,
    backgroundColor: '#E5E7EB',
    marginHorizontal: 8,
  },
  stepContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    marginBottom: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 32,
    textAlign: 'center',
  },
  questionContainer: {
    marginBottom: 24,
  },
  questionLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 12,
    lineHeight: 24,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#FFFFFF',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  timelineGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  timelineInput: {
    flex: 1,
    minWidth: '45%',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#FFFFFF',
  },
  checkboxGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  checkboxList: {
    gap: 12,
  },
  checkboxItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    borderRadius: 4,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  marginTop: {
    marginTop: 12,
  },
  navigationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: '#F3F4F6',
  },
  nextButton: {
    backgroundColor: '#2563EB',
  },
  submitButton: {
    backgroundColor: '#059669',
  },
  buttonDisabled: {
    backgroundColor: '#E5E7EB',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  backButtonText: {
    color: '#374151',
  },
  nextButtonText: {
    color: '#FFFFFF',
  },
  submitButtonText: {
    color: '#FFFFFF',
  },
  buttonTextDisabled: {
    color: '#9CA3AF',
  },
});

export default Onboarding; 